<template>
  <div class="map-container">
    <div class="map-header">
      <h2>Mapa de Ubicación</h2>
      <p class="map-description">Mapa de la emergencia</p>
    </div>
    <div class="map-content">
      <div ref="mapContainer" class="map-display"></div>
      <div class="markers-panel">
        <div class="markers-header">
          <h3>Marcadores y Polígonos</h3>
          <p class="markers-description">
            Añade puntos de interés y áreas al mapa
          </p>
        </div>

        <!-- Drawing Mode Toggle -->
        <div
          class="drawing-mode-toggle enhanced-toggle"
          style="
            display: flex !important;
            margin-bottom: 20px !important;
            background: #36393f !important;
            border-radius: 8px !important;
            padding: 4px !important;
            border: 1px solid #40444b !important;
            min-height: 56px !important;
          "
        >
          <button
            @click="setDrawingMode('marker')"
            class="mode-btn enhanced-mode-btn"
            :class="{ active: drawingMode === 'marker' }"
            type="button"
            :style="
              drawingMode === 'marker'
                ? 'background: #57f287 !important; color: #2f3136 !important;'
                : 'background: transparent; color: #b9bbbe;'
            "
            style="
              flex: 1 !important;
              padding: 12px 20px !important;
              border: none !important;
              cursor: pointer !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              gap: 8px !important;
              transition: all 0.2s ease !important;
              border-radius: 6px !important;
              font-weight: 500 !important;
              font-size: 14px !important;
              min-height: 48px !important;
            "
          >
            <span class="material-icons">location_on</span>
            Marcadores
          </button>
          <button
            @click="setDrawingMode('polygon')"
            class="mode-btn enhanced-mode-btn"
            :class="{ active: drawingMode === 'polygon' }"
            type="button"
            :style="
              drawingMode === 'polygon'
                ? 'background: #57f287 !important; color: #2f3136 !important;'
                : 'background: transparent; color: #b9bbbe;'
            "
            style="
              flex: 1 !important;
              padding: 12px 20px !important;
              border: none !important;
              cursor: pointer !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              gap: 8px !important;
              transition: all 0.2s ease !important;
              border-radius: 6px !important;
              font-weight: 500 !important;
              font-size: 14px !important;
              min-height: 48px !important;
            "
          >
            <span class="material-icons">square_foot</span>
            Polígonos
          </button>
        </div>
        <!-- Marker Form (shown when marker mode is active) -->
        <div v-if="drawingMode === 'marker'" class="add-marker-form">
          <input
            v-model="newMarker.label"
            placeholder="Etiqueta del marcador"
            class="marker-input"
            @keyup.enter="!isSelectingPosition && addMarker()"
          />
          <textarea
            v-model="newMarker.description"
            placeholder="Descripción (opcional)"
            class="marker-textarea"
            rows="2"
          ></textarea>

          <div class="color-picker">
            <label>Color del marcador:</label>
            <div class="color-options">
              <button
                v-for="color in markerColors"
                :key="color.value"
                @click="newMarker.color = color.value"
                class="color-btn"
                :class="{ active: newMarker.color === color.value }"
                :style="{ backgroundColor: color.value }"
                :title="color.name"
              ></button>
            </div>
          </div>

          <div class="position-controls">
            <button
              @click="togglePositionSelection"
              class="position-select-btn"
              :class="{ active: isSelectingPosition }"
              type="button"
            >
              <span class="material-icons">{{
                isSelectingPosition ? 'cancel' : 'location_on'
              }}</span>
              {{
                isSelectingPosition
                  ? 'Cancelar selección'
                  : 'Seleccionar posición'
              }}
            </button>

            <div v-if="selectedPosition" class="selected-position">
              <span class="material-icons">location_on</span>
              Posición seleccionada
            </div>
          </div>

          <button
            @click="addMarker"
            class="add-marker-btn"
            :disabled="
              !newMarker.label.trim() ||
              (isSelectingPosition && !selectedPosition)
            "
          >
            <span class="material-icons">add_location</span>
            Añadir Marcador
          </button>
        </div>

        <!-- Polygon Form (shown when polygon mode is active) -->
        <div v-if="drawingMode === 'polygon'" class="add-polygon-form">
          <input
            v-model="newPolygon.label"
            placeholder="Nombre del área"
            class="marker-input"
          />
          <textarea
            v-model="newPolygon.description"
            placeholder="Descripción del área (opcional)"
            class="marker-textarea"
            rows="2"
          ></textarea>

          <div class="color-picker">
            <label>Color del área:</label>
            <div class="color-options">
              <button
                v-for="color in polygonColors"
                :key="color.value"
                @click="newPolygon.color = color.value"
                class="color-btn"
                :class="{ active: newPolygon.color === color.value }"
                :style="{ backgroundColor: color.value }"
                :title="color.name"
              ></button>
            </div>
          </div>

          <div class="polygon-controls">
            <button
              @click="startDrawingPolygon"
              class="polygon-draw-btn"
              :class="{ active: isDrawingPolygon }"
              type="button"
            >
              <span class="material-icons">{{
                isDrawingPolygon ? 'stop' : 'draw'
              }}</span>
              {{ isDrawingPolygon ? 'Cancelar' : 'Dibujar área' }}
            </button>

            <div v-if="polygonPoints.length > 0" class="polygon-info">
              <span class="material-icons">info</span>
              {{ polygonPoints.length }} puntos dibujados
              <span v-if="polygonPoints.length >= 3">
                - {{ formatArea(calculatePolygonArea(polygonPoints)) }}
              </span>
            </div>
          </div>

          <button
            @click="addPolygon"
            class="add-polygon-btn"
            :disabled="!newPolygon.label || polygonPoints.length < 3"
            type="button"
          >
            <span class="material-icons">add</span>
            Añadir Área
          </button>
        </div>

        <div class="markers-list">
          <h4>Elementos del mapa</h4>
          <div
            v-if="
              customMarkers.length === 0 &&
              polygons.length === 0 &&
              !userLocation
            "
            class="no-markers"
          >
            No hay elementos añadidos
          </div>

          <!-- Current Location (always shown if available) -->
          <div v-if="userLocation" class="marker-item current-location-item">
            <div class="marker-info">
              <div class="marker-header">
                <span
                  class="material-icons marker-type-icon current-location-icon"
                  >my_location</span
                >
                <div class="marker-label">Tu ubicación actual</div>
              </div>
              <div class="marker-meta">
                <span class="marker-type current-location-type"
                  >Ubicación actual</span
                >
              </div>
            </div>
            <div class="marker-actions">
              <button
                @click="focusCurrentLocation"
                class="focus-btn"
                title="Centrar en tu ubicación"
              >
                <span class="material-icons">center_focus_strong</span>
              </button>
            </div>
          </div>

          <!-- Markers -->
          <div
            v-for="(marker, index) in customMarkers"
            :key="'marker-' + index"
            class="marker-item"
          >
            <div class="marker-info">
              <div class="marker-header">
                <span class="material-icons marker-type-icon">location_on</span>
                <div class="marker-label">{{ marker.label }}</div>
              </div>
              <div v-if="marker.description" class="marker-description">
                {{ marker.description }}
              </div>
              <div class="marker-meta">
                <span class="marker-type">Marcador</span>
              </div>
            </div>
            <div class="marker-actions">
              <button
                @click="focusMarker(marker)"
                class="focus-btn"
                title="Centrar en el mapa"
              >
                <span class="material-icons">center_focus_strong</span>
              </button>
              <button
                @click="removeMarker(index)"
                class="remove-btn"
                title="Eliminar marcador"
              >
                <span class="material-icons">delete</span>
              </button>
            </div>
          </div>

          <!-- Polygons -->
          <div
            v-for="(polygon, index) in polygons"
            :key="'polygon-' + index"
            class="marker-item polygon-item"
          >
            <div class="marker-info">
              <div class="marker-header">
                <span class="material-icons marker-type-icon">square_foot</span>
                <div class="marker-label">{{ polygon.label }}</div>
              </div>
              <div v-if="polygon.description" class="marker-description">
                {{ polygon.description }}
              </div>
              <div class="marker-meta">
                <span class="marker-type">Área</span>
                <span
                  class="polygon-color"
                  :style="{ backgroundColor: polygon.color }"
                ></span>
                <span class="polygon-area">{{
                  formatArea(polygon.area || 0)
                }}</span>
              </div>
            </div>
            <div class="marker-actions">
              <button
                @click="focusPolygon(polygon)"
                class="focus-btn"
                title="Centrar en el área"
              >
                <span class="material-icons">center_focus_strong</span>
              </button>
              <button
                @click="removePolygon(index)"
                class="remove-btn"
                title="Eliminar área"
              >
                <span class="material-icons">delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner">
        <span class="material-icons">location_searching</span>
        <p>Obteniendo tu ubicación...</p>
      </div>
    </div>
    <div v-if="error" class="error-message">
      <span class="material-icons">error</span>
      <p>{{ error }}</p>
      <button @click="dismissError" class="error-close-btn" title="Cerrar">
        <span class="material-icons">close</span>
      </button>
    </div>
  </div>
</template>

<script>
import L from 'leaflet'

export default {
  name: 'MapView',
  data() {
    return {
      map: null,
      loading: true,
      error: null,
      userLocation: null,
      userMarker: null,
      customMarkers: [],
      newMarker: {
        label: '',
        description: '',
        color: '#3388ff',
      },
      markerColors: [
        { name: 'Azul', value: '#3388ff' },
        { name: 'Rojo', value: '#ff3333' },
        { name: 'Verde', value: '#33ff33' },
        { name: 'Amarillo', value: '#ffff33' },
        { name: 'Naranja', value: '#ff8833' },
        { name: 'Morado', value: '#8833ff' },
        { name: 'Rosa', value: '#ff33ff' },
        { name: 'Cian', value: '#33ffff' },
      ],
      isSelectingPosition: false,
      selectedPosition: null,
      tempMarker: null,
      drawingMode: 'marker', // 'marker' or 'polygon'
      // Polygon data
      polygons: [],
      newPolygon: {
        label: '',
        description: '',
        color: '#3388ff',
      },
      polygonColors: [
        { name: 'Azul', value: '#3388ff' },
        { name: 'Rojo', value: '#ff3333' },
        { name: 'Verde', value: '#33ff33' },
        { name: 'Amarillo', value: '#ffff33' },
        { name: 'Naranja', value: '#ff8833' },
        { name: 'Morado', value: '#8833ff' },
        { name: 'Rosa', value: '#ff33ff' },
        { name: 'Cian', value: '#33ffff' },
      ],
      isDrawingPolygon: false,
      polygonPoints: [], // Points being drawn
      activePolygonLayer: null, // Current polygon being drawn
    }
  },
  watch: {
    // Watch for when the map tab becomes visible
    '$parent.activeTab'(newTab) {
      if (newTab === 'mapa' && this.map) {
        // Delay map resize to ensure the container is visible
        this.$nextTick(() => {
          setTimeout(() => {
            this.map.invalidateSize()
          }, 100)
        })
      }
    },
  },
  mounted() {
    // Add a delay for PWA standalone mode to ensure proper rendering
    if (
      window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches
    ) {
      // In PWA mode, add extra delay and resize handling
      this.$nextTick(() => {
        setTimeout(() => {
          this.initializeMap()
          // Additional resize fix for PWA
          setTimeout(() => {
            if (this.map) {
              this.map.invalidateSize()
            }
          }, 500)
        }, 100)
      })
    } else {
      // Normal web mode
      this.$nextTick(() => {
        this.initializeMap()
      })
    }

    // Add window resize listener for PWA mode
    this.handleResize = () => {
      if (this.map) {
        setTimeout(() => {
          this.map.invalidateSize()
        }, 100)
      }
    }
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('orientationchange', this.handleResize)
  },
  beforeUnmount() {
    // Clean up event listeners
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
      window.removeEventListener('orientationchange', this.handleResize)
    }

    if (this.map) {
      this.map.remove()
    }
    if (this.userMarker) {
      this.userMarker = null
    }
    if (this.tempMarker) {
      this.tempMarker = null
    }
  },
  methods: {
    initializeMap() {
      this.getUserLocation()
    },

    getUserLocation() {
      if (!navigator.geolocation) {
        this.error = 'La geolocalización no está soportada en este navegador'
        this.loading = false
        this.createDefaultMap()
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          this.createMap()
          this.loading = false
        },
        (error) => {
          console.error('Error getting location:', error)
          this.error = `Error obteniendo ubicación: ${error.message}`
          this.loading = false
          this.createDefaultMap()
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 600000, // 10 minutes
        }
      )
    },

    createMap() {
      const center = this.userLocation || { lat: 40.4168, lng: -3.7038 } // Default to Madrid

      this.map = L.map(this.$refs.mapContainer).setView(
        [center.lat, center.lng],
        15
      )

      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:
          '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(this.map)

      // Add user location marker if available
      if (this.userLocation) {
        this.userMarker = L.circleMarker(
          [this.userLocation.lat, this.userLocation.lng],
          {
            radius: 8,
            fillColor: '#007bff',
            color: '#ffffff',
            weight: 2,
            opacity: 1,
            fillOpacity: 0.8,
          }
        )
          .addTo(this.map)
          .bindPopup('Tu ubicación actual')
          .openPopup()
      }

      // Add click handler for position selection
      this.map.on('click', this.onMapClick)

      this.loadMarkers()
      this.loadPolygons()
    },

    createDefaultMap() {
      // Create map with default location (Santiago, Chile)
      const defaultLocation = [-33.4375, -70.65]

      this.map = L.map(this.$refs.mapContainer).setView(defaultLocation, 6)

      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:
          '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(this.map)

      // Add click handler for position selection
      this.map.on('click', this.onMapClick)

      this.loadMarkers()
      this.loadPolygons()
    },

    onMapClick(e) {
      if (this.isSelectingPosition) {
        this.selectedPosition = {
          lat: parseFloat(e.latlng.lat),
          lng: parseFloat(e.latlng.lng),
        }

        // Remove previous temporary marker if exists
        if (this.tempMarker) {
          this.map.removeLayer(this.tempMarker)
        }

        // Add temporary marker to show selected position
        this.tempMarker = L.marker(
          [this.selectedPosition.lat, this.selectedPosition.lng],
          {
            icon: this.createMarkerIcon(this.newMarker.color),
          }
        ).addTo(this.map)
      }
    },

    togglePositionSelection() {
      this.isSelectingPosition = !this.isSelectingPosition

      if (!this.isSelectingPosition) {
        // Clear selection
        this.selectedPosition = null
        if (this.tempMarker) {
          this.map.removeLayer(this.tempMarker)
          this.tempMarker = null
        }
      }
    },

    addMarker() {
      if (!this.newMarker.label.trim() || !this.map) return

      let position
      if (this.selectedPosition) {
        position = this.selectedPosition
      } else {
        // Use map center as fallback
        const center = this.map.getCenter()
        position = { lat: center.lat, lng: center.lng }
      }

      const marker = {
        id: Date.now(),
        label: this.newMarker.label.trim(),
        description: this.newMarker.description.trim(),
        color: this.newMarker.color,
        lat: parseFloat(position.lat),
        lng: parseFloat(position.lng),
      }

      this.customMarkers.push(marker)
      this.addMarkerToMap(marker)
      this.saveMarkers()

      // Reset form and selection
      this.newMarker = {
        label: '',
        description: '',
        color: '#3388ff',
      }
      this.selectedPosition = null
      this.isSelectingPosition = false
      if (this.tempMarker) {
        this.map.removeLayer(this.tempMarker)
        this.tempMarker = null
      }
    },

    addMarkerToMap(marker) {
      // Ensure coordinates are numbers and properly formatted
      const lat = parseFloat(marker.lat)
      const lng = parseFloat(marker.lng)

      // Validate coordinates
      if (isNaN(lat) || isNaN(lng)) {
        console.error('Invalid coordinates for marker:', marker)
        return
      }

      const leafletMarker = L.marker([lat, lng], {
        icon: this.createMarkerIcon(marker.color || '#3388ff'),
      })
        .bindPopup(this.createMarkerPopupContent(marker))
        .addTo(this.map)

      marker.leafletMarker = leafletMarker
    },

    createMarkerIcon(color) {
      // Create a custom SVG icon with the selected color
      const svgIcon = `
        <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 9.375 12.5 28.125 12.5 28.125S25 21.875 25 12.5C25 5.596 19.404 0 12.5 0z" fill="${color}" stroke="#ffffff" stroke-width="1"/>
          <circle cx="12.5" cy="12.5" r="4" fill="#ffffff"/>
        </svg>
      `

      return L.divIcon({
        html: svgIcon,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        className: 'custom-marker-icon',
      })
    },

    createMarkerPopupContent(marker) {
      let content = `<strong>${marker.label}</strong>`
      if (marker.description) {
        content += `<br>${marker.description}`
      }
      return content
    },

    focusMarker(marker) {
      if (this.map && marker.leafletMarker) {
        this.map.setView([marker.lat, marker.lng], 15)
        marker.leafletMarker.openPopup()
      }
    },

    focusPolygon(polygon) {
      if (this.map && polygon.leafletPolygon) {
        // Calculate the center of the polygon
        const bounds = polygon.leafletPolygon.getBounds()
        this.map.fitBounds(bounds, { padding: [20, 20] })
        polygon.leafletPolygon.openPopup()
      }
    },

    removeMarker(index) {
      const marker = this.customMarkers[index]
      if (marker.leafletMarker) {
        this.map.removeLayer(marker.leafletMarker)
      }
      this.customMarkers.splice(index, 1)
      this.saveMarkers()
    },

    saveMarkers() {
      // Save markers without the leafletMarker property
      const markersToSave = this.customMarkers.map((marker) => ({
        id: marker.id,
        label: marker.label,
        description: marker.description,
        color: marker.color,
        lat: marker.lat,
        lng: marker.lng,
      }))
      localStorage.setItem('nexo-vue-markers', JSON.stringify(markersToSave))
    },

    loadMarkers() {
      const saved = localStorage.getItem('nexo-vue-markers')
      if (saved) {
        const savedMarkers = JSON.parse(saved)
        savedMarkers.forEach((markerData) => {
          // Ensure coordinates are properly formatted as numbers
          const marker = {
            ...markerData,
            lat: parseFloat(markerData.lat),
            lng: parseFloat(markerData.lng),
          }

          // Only add marker if coordinates are valid
          if (!isNaN(marker.lat) && !isNaN(marker.lng)) {
            // Ensure color is set for existing markers
            if (!marker.color) {
              marker.color = '#3388ff'
            }
            this.customMarkers.push(marker)
            this.addMarkerToMap(marker)
          }
        })
      }
    },

    dismissError() {
      this.error = null
    },

    // Drawing mode methods
    setDrawingMode(mode) {
      // Stop any current drawing when switching modes
      if (this.isDrawingPolygon) {
        this.stopPolygonDrawing()
      }
      if (this.isSelectingPosition) {
        this.togglePositionSelection()
      }

      this.drawingMode = mode
    },

    // Polygon methods
    startDrawingPolygon() {
      this.isDrawingPolygon = !this.isDrawingPolygon

      if (this.isDrawingPolygon) {
        // Start drawing mode
        this.polygonPoints = []
        this.map.getContainer().style.cursor = 'crosshair'

        // Add click listener for polygon points
        this.map.on('click', this.addPolygonPoint)
      } else {
        // Stop drawing mode
        this.stopPolygonDrawing()
      }
    },

    addPolygonPoint(e) {
      if (!this.isDrawingPolygon) return

      const point = [e.latlng.lat, e.latlng.lng]
      this.polygonPoints.push(point)

      // Add a temporary marker for the point
      const marker = L.circleMarker(e.latlng, {
        radius: 4,
        color: this.newPolygon.color,
        fillColor: this.newPolygon.color,
        fillOpacity: 0.8,
      }).addTo(this.map)

      // Store marker reference for cleanup
      if (!this.activePolygonLayer) {
        this.activePolygonLayer = L.layerGroup().addTo(this.map)
      }
      this.activePolygonLayer.addLayer(marker)

      // Draw polygon if we have enough points
      if (this.polygonPoints.length >= 2) {
        this.updatePolygonPreview()
      }
    },

    updatePolygonPreview() {
      // Remove existing preview polygon
      if (this.activePolygonLayer) {
        this.activePolygonLayer.eachLayer((layer) => {
          if (layer instanceof L.Polygon) {
            this.activePolygonLayer.removeLayer(layer)
          }
        })
      }

      // Add new preview polygon
      if (this.polygonPoints.length >= 3) {
        const polygon = L.polygon(this.polygonPoints, {
          color: this.newPolygon.color,
          fillColor: this.newPolygon.color,
          fillOpacity: 0.2,
          weight: 2,
        })
        this.activePolygonLayer.addLayer(polygon)
      }
    },

    stopPolygonDrawing() {
      this.isDrawingPolygon = false
      this.map.getContainer().style.cursor = ''
      this.map.off('click', this.addPolygonPoint)

      // Clean up temporary drawing elements
      if (this.activePolygonLayer) {
        this.map.removeLayer(this.activePolygonLayer)
        this.activePolygonLayer = null
      }
    },

    addPolygon() {
      if (!this.newPolygon.label || this.polygonPoints.length < 3) {
        return
      }

      const polygon = {
        id: Date.now(),
        label: this.newPolygon.label,
        description: this.newPolygon.description,
        color: this.newPolygon.color,
        points: [...this.polygonPoints],
        area: this.calculatePolygonArea(this.polygonPoints),
        createdAt: new Date().toISOString(),
      }

      this.polygons.push(polygon)
      this.addPolygonToMap(polygon)
      this.savePolygons()

      // Reset form and drawing state
      this.newPolygon = {
        label: '',
        description: '',
        color: '#3388ff',
      }
      this.stopPolygonDrawing()

      // Log activity
      this.$root.$emit('activity-logged', {
        type: 'map',
        subtype: 'polygon_created',
        description: `Área "${polygon.label}" creada en el mapa`,
        details: {
          polygonId: polygon.id,
          pointCount: polygon.points.length,
        },
      })
    },

    addPolygonToMap(polygon) {
      if (!this.map) return

      const leafletPolygon = L.polygon(polygon.points, {
        color: polygon.color,
        fillColor: polygon.color,
        fillOpacity: 0.2,
        weight: 2,
      }).addTo(this.map)

      // Create popup content - information only, no delete button
      const popupContent = `
        <div class="polygon-popup">
          <h3>${polygon.label}</h3>
          ${polygon.description ? `<p>${polygon.description}</p>` : ''}
          <div class="polygon-info-details">
            <div class="polygon-meta">
              <span class="polygon-area-info">
                <span class="material-icons">square_foot</span>
                ${this.formatArea(polygon.area || 0)}
              </span>
              <span class="polygon-color-info">
                <span class="color-indicator" style="background-color: ${
                  polygon.color
                }"></span>
                Área marcada
              </span>
            </div>
          </div>
        </div>
      `

      leafletPolygon.bindPopup(popupContent)

      // Store reference
      polygon.leafletPolygon = leafletPolygon
    },

    removePolygon(index) {
      const polygon = this.polygons[index]
      if (polygon.leafletPolygon) {
        this.map.removeLayer(polygon.leafletPolygon)
      }
      this.polygons.splice(index, 1)
      this.savePolygons()

      // Log activity
      this.$root.$emit('activity-logged', {
        type: 'map',
        subtype: 'polygon_deleted',
        description: `Área "${polygon.label}" eliminada del mapa`,
        details: {
          polygonId: polygon.id,
        },
      })
    },

    savePolygons() {
      const polygonsToSave = this.polygons.map((polygon) => ({
        id: polygon.id,
        label: polygon.label,
        description: polygon.description,
        color: polygon.color,
        points: polygon.points,
        area: polygon.area,
        createdAt: polygon.createdAt,
      }))
      localStorage.setItem('nexo-vue-polygons', JSON.stringify(polygonsToSave))
    },

    loadPolygons() {
      const saved = localStorage.getItem('nexo-vue-polygons')
      if (saved) {
        const savedPolygons = JSON.parse(saved)
        savedPolygons.forEach((polygonData) => {
          // Calculate area for existing polygons that don't have it
          if (!polygonData.area && polygonData.points) {
            polygonData.area = this.calculatePolygonArea(polygonData.points)
          }
          this.polygons.push(polygonData)
          this.addPolygonToMap(polygonData)
        })
      }
    },

    // Helper method to calculate polygon area using the shoelace formula
    calculatePolygonArea(points) {
      if (points.length < 3) return 0

      // Convert lat/lng to meters using approximate conversion
      // This is a simplified calculation for display purposes
      const latToMeters = 111000 // roughly 111 km per degree latitude

      // For longitude, we need to account for latitude (cos factor)
      const avgLat =
        points.reduce((sum, point) => sum + point[0], 0) / points.length
      const lngToMeters = latToMeters * Math.cos((avgLat * Math.PI) / 180)

      // Convert points to meter coordinates relative to first point
      const meterPoints = points.map((point) => [
        (point[0] - points[0][0]) * latToMeters,
        (point[1] - points[0][1]) * lngToMeters,
      ])

      // Apply shoelace formula
      let area = 0
      for (let i = 0; i < meterPoints.length; i++) {
        const j = (i + 1) % meterPoints.length
        area += meterPoints[i][0] * meterPoints[j][1]
        area -= meterPoints[j][0] * meterPoints[i][1]
      }

      return Math.abs(area / 2)
    },

    // Helper method to format area with appropriate units
    formatArea(area) {
      if (area < 1) {
        return '< 1 m²'
      } else if (area < 10000) {
        return `${Math.round(area)} m²`
      } else if (area < 1000000) {
        return `${(area / 10000).toFixed(2)} ha`
      } else {
        return `${(area / 1000000).toFixed(2)} km²`
      }
    },

    // Focus on current user location
    focusCurrentLocation() {
      if (this.map && this.userLocation) {
        this.map.setView([this.userLocation.lat, this.userLocation.lng], 15)
        if (this.userMarker) {
          this.userMarker.openPopup()
        }
      }
    },
  },
}
</script>

<style>
.map-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #202225;
  position: relative;
}

/* PWA-specific fixes for map rendering */
@media (display-mode: standalone) {
  .map-container {
    min-height: calc(100vh - env(safe-area-inset-top, 0px));
  }

  .map-display {
    /* Force hardware acceleration for better rendering in PWA */
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }
}

.map-header {
  padding: 20px 20px 20px 80px;
  background-color: #2f3136;
  border-bottom: 1px solid #40444b;
}

.map-header h2 {
  margin: 0 0 8px 0;
  color: #ffffff;
  font-size: 24px;
}

.map-description {
  margin: 0;
  color: #b9bbbe;
  font-size: 14px;
}

.map-content {
  display: flex;
  min-height: 600px;
  height: 80vh;
}

.map-display {
  flex: 2;
  min-height: 400px;
  background-color: #36393f;
}

.markers-panel {
  flex: 1;
  background-color: #2f3136;
  border-left: 1px solid #40444b;
  display: flex;
  flex-direction: column;
}

.markers-header {
  padding: 20px;
  border-bottom: 1px solid #40444b;
}

.markers-header h3 {
  margin: 0 0 8px 0;
  color: #ffffff;
  font-size: 18px;
}

.markers-description {
  margin: 0;
  color: #b9bbbe;
  font-size: 14px;
}

.add-marker-form {
  padding: 20px;
  border-bottom: 1px solid #40444b;
}

.marker-input,
.marker-textarea {
  width: 100%;
  padding: 10px;
  margin-bottom: 12px;
  border: 1px solid #40444b;
  border-radius: 4px;
  background-color: #40444b;
  color: #dcddde;
  font-size: 14px;
  box-sizing: border-box;
  transition: border-color 0.2s;
  font-family: inherit;
  resize: vertical;
}

.marker-input:focus,
.marker-textarea:focus {
  border-color: #5865f2;
  outline: none;
}

.marker-input::placeholder,
.marker-textarea::placeholder {
  color: #72767d;
}

.add-marker-btn {
  width: 100%;
  padding: 12px 16px;
  background-color: #57f287;
  color: #2f3136;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.2s;
}

.add-marker-btn:hover:not(:disabled) {
  background-color: #4ade80;
}

.add-marker-btn:disabled {
  background-color: #4f545c;
  cursor: not-allowed;
  opacity: 0.6;
}

.position-controls {
  margin-bottom: 12px;
}

.position-select-btn {
  width: 100%;
  padding: 10px 16px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.2s;
  margin-bottom: 8px;
  font-size: 14px;
}

.position-select-btn:hover {
  background-color: #5a6268;
}

.position-select-btn.active {
  background-color: #dc3545;
}

.position-select-btn.active:hover {
  background-color: #c82333;
}

.selected-position {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #00b894;
  font-size: 14px;
  font-weight: 500;
  padding: 8px;
  background-color: rgba(0, 184, 148, 0.1);
  border-radius: 4px;
  border: 1px solid rgba(0, 184, 148, 0.3);
}

.markers-list {
  padding: 20px;
}

.markers-list h4 {
  margin: 0 0 16px 0;
  color: #ffffff;
  font-size: 16px;
}

.no-markers {
  color: #72767d;
  font-style: italic;
  text-align: center;
  padding: 20px;
}

.marker-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px;
  background-color: #40444b;
  border-radius: 8px;
  margin-bottom: 8px;
  border-left: 4px solid #5865f2;
}

.polygon-item {
  border-left-color: #57f287;
}

.current-location-item {
  border-left-color: #007bff;
  background-color: #4a5568;
}

.marker-info {
  flex: 1;
  margin-right: 12px;
}

.marker-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.marker-type-icon {
  font-size: 18px;
  color: #dcddde;
}

.marker-label {
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0;
}

.marker-description {
  font-size: 13px;
  color: #b9bbbe;
  line-height: 1.4;
  margin-bottom: 8px;
  margin-top: 4px;
}

.marker-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.marker-type {
  background-color: #2f3136;
  color: #dcddde;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.current-location-type {
  background-color: #007bff;
  color: #ffffff;
}

.current-location-icon {
  color: #007bff !important;
}

.polygon-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #dcddde;
}

.polygon-points {
  color: #b9bbbe;
  font-size: 10px;
}

.polygon-area {
  color: #b9bbbe;
  font-size: 10px;
  font-weight: 600;
}

.marker-actions {
  display: flex;
  gap: 4px;
}

.focus-btn,
.remove-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  width: 28px;
  height: 28px;
}

.focus-btn {
  color: #5865f2;
}

.focus-btn:hover {
  background-color: rgba(88, 101, 242, 0.1);
}

.remove-btn {
  color: #ed4245;
}

.remove-btn:hover {
  background-color: rgba(237, 66, 69, 0.1);
}

.focus-btn .material-icons,
.remove-btn .material-icons {
  font-size: 18px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(47, 49, 54, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #ffffff;
}

.loading-spinner .material-icons {
  font-size: 48px;
  animation: pulse 1.5s ease-in-out infinite;
}

.loading-spinner p {
  margin: 0;
  font-size: 16px;
}

.error-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #ed4245;
  color: white;
  padding: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 1000;
}

.error-message .material-icons {
  font-size: 20px;
}

.error-close-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  transition: background-color 0.2s;
}

.error-close-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.error-close-btn .material-icons {
  font-size: 18px;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Leaflet map styles override */
:deep(.leaflet-container) {
  background-color: #36393f;
}

:deep(.leaflet-popup-content-wrapper) {
  background-color: #2f3136;
  color: #dcddde;
}

:deep(.leaflet-popup-tip) {
  background-color: #2f3136;
}

@media (max-width: 768px) {
  .map-header {
    padding: 16px 16px 16px 70px;
  }

  .map-header h2 {
    font-size: 20px;
  }

  .map-content {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
  }

  .map-display {
    flex: none;
    height: 50vh;
    min-height: 300px;
  }

  .markers-panel {
    flex: none;
    border-left: none;
    border-top: 1px solid #40444b;
  }

  .markers-header {
    padding: 16px;
  }

  .add-marker-form {
    padding: 16px;
  }

  .markers-list {
    padding: 16px;
  }
}

/* Polygon Styles - Flat Design */
.drawing-mode-toggle,
.enhanced-toggle {
  display: flex !important;
  margin-bottom: 20px !important;
  background: #36393f !important;
  border-radius: 8px !important;
  padding: 4px !important;
  border: 1px solid #40444b !important;
  min-height: 56px !important;
  width: 100% !important;
  box-sizing: border-box !important;
}

.mode-btn,
.enhanced-mode-btn {
  flex: 1 !important;
  padding: 12px 20px !important;
  background-color: transparent !important;
  border: none !important;
  color: #b9bbbe !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;
  transition: all 0.2s ease !important;
  border-radius: 6px !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  min-height: 48px !important;
  white-space: nowrap !important;
  box-sizing: border-box !important;
}

.mode-btn .material-icons,
.enhanced-mode-btn .material-icons {
  font-size: 18px !important;
}

.mode-btn:hover:not(.active),
.enhanced-mode-btn:hover:not(.active) {
  background: #40444b !important;
  color: #dcddde !important;
}

.mode-btn.active,
.enhanced-mode-btn.active {
  background: #57f287 !important;
  color: #2f3136 !important;
}

.add-polygon-form {
  padding: 16px;
}

.color-picker {
  margin-bottom: 16px;
  padding: 16px;
  background: #40444b;
  border-radius: 6px;
  border: 1px solid #4f545c;
}

.color-picker label {
  display: block;
  margin-bottom: 12px;
  color: #dcddde;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.color-options {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

.color-btn {
  width: 40px;
  height: 40px;
  border: 2px solid transparent;
  border-radius: 50%;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.color-btn:hover {
  border-color: #dcddde;
}

.color-btn.active {
  border-color: #ffffff;
}

.polygon-controls {
  margin-bottom: 12px;
}

.polygon-draw-btn {
  width: 100%;
  padding: 10px 16px;
  background: #6c757d;
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.2s;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 14px;
}

.polygon-draw-btn:hover {
  background: #5a6268;
}

.polygon-draw-btn.active {
  background: #dc3545;
}

.polygon-draw-btn.active:hover {
  background: #c82333;
}

.polygon-info {
  padding: 12px 16px;
  background: #40444b;
  border-radius: 6px;
  color: #dcddde;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #5865f2;
}

.polygon-info .material-icons {
  color: #5865f2;
  font-size: 16px;
}

.add-polygon-btn {
  width: 100%;
  padding: 12px 16px;
  background: #57f287;
  border: none;
  border-radius: 4px;
  color: #2f3136;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.2s;
  font-weight: 600;
}

.add-polygon-btn:hover:not(:disabled) {
  background: #4ade80;
}

.add-polygon-btn:disabled {
  background: #4f545c;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Polygon popup styles */
.polygon-popup h3 {
  margin: 0 0 8px 0;
  color: #2f3136;
  font-size: 16px;
  font-weight: 600;
}

.polygon-popup p {
  margin: 0 0 12px 0;
  color: #4f545c;
  font-size: 14px;
  line-height: 1.4;
}

.polygon-info-details {
  margin-top: 8px;
}

.polygon-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.polygon-points-info,
.polygon-area-info,
.polygon-color-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #4f545c;
}

.polygon-points-info .material-icons,
.polygon-area-info .material-icons {
  font-size: 14px;
  color: #5865f2;
}

.color-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #4f545c;
}

/* Custom marker icon styles */
:deep(.custom-marker-icon) {
  background: none !important;
  border: none !important;
}
</style>
